import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["open", "onDismiss", "onEscape", "unmountChildrenOnClose", "ignoreEscape", "ignoreBackdropClick", "hideBackdrop", "className", "backdropClassName", "backdropStyles", "children", "position", "disabledFocusLock", "disabledScrollLock", "width", "htmlProps"];
import { Fragment, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { clsx } from 'clsx';
import { useIsClient, useMediaQuery, useMountChildrenValidation } from '@crehana/react-hooks';
import { KEY_NAMES } from "./constants";
import { Position } from "./types";

/* This is a functional component called `Drawer` that renders a drawer/modal component with various
props and options. It uses React hooks such as `useRef`, `useEffect`, and `useMediaQuery` to handle
state and events. It also uses third-party libraries such as `clsx`, `ReactDOM`, and `FocusLock` to
manage CSS classes, render the component in a portal, and handle focus locking. The component takes
in various props such as `open`, `onDismiss`, `position`, `width`, and `children` to customize the
behavior and appearance of the drawer. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var Drawer = function Drawer(_ref) {
  var _ref$open = _ref.open,
    open = _ref$open === void 0 ? false : _ref$open,
    onDismiss = _ref.onDismiss,
    onEscape = _ref.onEscape,
    unmountChildrenOnClose = _ref.unmountChildrenOnClose,
    _ref$ignoreEscape = _ref.ignoreEscape,
    ignoreEscape = _ref$ignoreEscape === void 0 ? false : _ref$ignoreEscape,
    _ref$ignoreBackdropCl = _ref.ignoreBackdropClick,
    ignoreBackdropClick = _ref$ignoreBackdropCl === void 0 ? false : _ref$ignoreBackdropCl,
    _ref$hideBackdrop = _ref.hideBackdrop,
    hideBackdrop = _ref$hideBackdrop === void 0 ? false : _ref$hideBackdrop,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$backdropClassNam = _ref.backdropClassName,
    backdropClassName = _ref$backdropClassNam === void 0 ? '' : _ref$backdropClassNam,
    backdropStyles = _ref.backdropStyles,
    children = _ref.children,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? Position.right : _ref$position,
    _ref$disabledFocusLoc = _ref.disabledFocusLock,
    disabledFocusLock = _ref$disabledFocusLoc === void 0 ? true : _ref$disabledFocusLoc,
    disabledScrollLock = _ref.disabledScrollLock,
    widthProp = _ref.width,
    htmlProps = _ref.htmlProps,
    drawerProps = _objectWithoutProperties(_ref, _excluded);
  var drawerRef = useRef(null);
  var _useMediaQuery = useMediaQuery('sm'),
    isSm = _useMediaQuery.matches;
  var _useMediaQuery2 = useMediaQuery('md'),
    isMd = _useMediaQuery2.matches;
  var _useMediaQuery3 = useMediaQuery('lg'),
    isLg = _useMediaQuery3.matches;
  var isClient = useIsClient();
  var shouldMountChildren = useMountChildrenValidation({
    isOpen: open,
    unmountChildrenOnClose: unmountChildrenOnClose
  });

  /**
   * This function handles the keydown event and checks if the pressed key is the escape key, and then
   * executes the corresponding functions.
   * @param event - The `event` parameter is a `React.KeyboardEvent` object that represents a keyboard
   * event, such as a key press or release. It is passed to the `handleKeyDown` function as an
   * argument.
   */
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === KEY_NAMES.ESCAPE) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (!ignoreEscape && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };
  var POSITIONS = _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, Position.bottom, {
    transform: "translateY(".concat(open ? 0 : 100, "%)"),
    absolutePosition: 'tw-left-0 tw-right-0 tw-bottom-0',
    className: ''
  }), Position.top, {
    transform: "translateY(".concat(open ? 0 : -100, "%)"),
    absolutePosition: 'tw-left-0 tw-right-0 tw-top-0',
    className: ''
  }), Position.right, {
    transform: "translateX(".concat(open ? 0 : 100, "%)"),
    absolutePosition: 'tw-right-0 tw-bottom-0 tw-top-0',
    className: 'tw-h-screen'
  }), Position.left, {
    transform: "translateX(".concat(open ? 0 : -100, "%)"),
    absolutePosition: 'tw-left-0 tw-top-0 tw-bottom-0',
    className: 'tw-h-screen'
  });
  useEffect(function () {
    var onClickOutside = function onClickOutside(event) {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && onDismiss) {
        onDismiss();
      }
    };
    if (open) {
      document.addEventListener('click', onClickOutside);
    }
    return function () {
      document.removeEventListener('click', onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!isClient) {
    return null;
  }
  var defaultWidth = position === 'top' || position === 'bottom' ? '100vw' : {
    xs: '80vw',
    sm: '33vw',
    md: '25vw',
    lg: '25vw'
  };
  var width = widthProp || defaultWidth;

  /**
   * This function returns a chosen width based on certain conditions, with a fallback value if none of
   * the conditions are met.
   * @returns The function `getChosenWidth` returns a string value. The returned value is determined by
   * the conditions inside the function. If the `width` variable is a string, it will be returned.
   * Otherwise, if the `isSm`, `isMd`, or `isLg` variables are true, the corresponding width value from
   * the `width` object will be returned. If none of these
   */
  var getChosenWidth = function getChosenWidth() {
    var fallbackWidth = '80vw';
    if (typeof width === 'string') return width;
    if (isSm) return width.sm;
    if (isMd) return width.md;
    if (isLg) return width.lg;
    return width.xs || fallbackWidth;
  };
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsxs(Fragment, {
    children: [!hideBackdrop && /*#__PURE__*/_jsx("div", {
      className: clsx('tw-fixed tw-z-dialog tw-inset-0 tw-bg-neutral-dark-800 tw-opacity-80', open ? 'tw-visible tw-opacity-100' : 'tw-hidden tw-opacity-0', backdropClassName),
      style: backdropStyles,
      onClick: ignoreBackdropClick ? undefined : onDismiss,
      onKeyDown: handleKeyDown,
      tabIndex: -1,
      "data-testid": "backdrop"
    }), /*#__PURE__*/_jsx(FocusLock, {
      disabled: disabledFocusLock || open,
      returnFocus: true,
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        enabled: !disabledScrollLock && open,
        forwardProps: true,
        children: /*#__PURE__*/_jsx("div", {
          className: clsx('tw-fixed tw-z-dialog tw-transition-opacity tw-duration-150 dark:tw-bg-neutral-dark-800 dark:tw-text-neutral-light-100 tw-bg-neutral-light-100 tw-shadow-02  tw-text-neutral-light-800 ', POSITIONS[position].className, POSITIONS[position].absolutePosition, open ? 'tw-visible tw-opacity-100' : 'tw-hidden tw-opacity-0'),
          style: {
            transform: POSITIONS[position].transform,
            width: getChosenWidth()
          },
          ref: drawerRef,
          children: /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread(_objectSpread({
            className: clsx("tw-w-full tw-max-h-full focus:tw-outline-none", className)
          }, htmlProps), drawerProps), {}, {
            children: shouldMountChildren && children
          }))
        })
      })
    })]
  }), document.body);
};